/* ==========================================================================
   Patient Form Component
   Task F14: Patient information section for prescription form
   Supports both human and animal (veterinary) patient types
   ========================================================================== */

import { useFormContext, Controller } from 'react-hook-form'
import { useEffect } from 'react'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { FormField } from '@/components/ui/FormField'
import { AddressFields } from './AddressFields'
import { validatePostalCode } from '@/utils/countries'
import { AlertTriangle } from 'lucide-react'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface PatientFormData {
  patient_type: 'human' | 'animal'
  // Human patient fields
  patient_first_name: string
  patient_last_name: string
  patient_middle_name: string
  patient_dob: string // YYYY-MM-DD format
  patient_gender: 'male' | 'female' | 'nonbinary' | 'unknown'
  patient_phone: string
  patient_email: string // optional; the owner's email for an animal patient
  patient_address_street: string
  patient_address_city: string
  patient_address_state: string
  patient_address_zip: string
  patient_allergies: string
  no_known_allergies: boolean
  current_medications: string
  no_current_medications: boolean
  // Animal patient fields
  animal_name: string
  animal_species: string
  animal_breed: string
  owner_first_name: string
  owner_last_name: string
  owner_phone: string
  patient_weight: string
  patient_weight_unit: string
  animal_dob_unknown: boolean
}

export const patientFormDefaultValues: PatientFormData = {
  patient_type: 'human',
  patient_first_name: '',
  patient_last_name: '',
  patient_middle_name: '',
  patient_dob: '',
  patient_gender: 'unknown',
  patient_phone: '',
  patient_email: '',
  patient_address_street: '',
  patient_address_city: '',
  patient_address_state: '',
  patient_address_zip: '',
  patient_allergies: '',
  no_known_allergies: false,
  current_medications: '',
  no_current_medications: false,
  animal_name: '',
  animal_species: '',
  animal_breed: '',
  owner_first_name: '',
  owner_last_name: '',
  owner_phone: '',
  patient_weight: '',
  patient_weight_unit: 'lbs',
  animal_dob_unknown: false,
}

/* --------------------------------------------------------------------------
   Utilities
   -------------------------------------------------------------------------- */

/**
 * Format phone number as user types: (XXX) XXX-XXXX
 */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '')
  const trimmed = digits.slice(0, 10)

  if (trimmed.length === 0) return ''
  if (trimmed.length <= 3) return `(${trimmed}`
  if (trimmed.length <= 6) return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`
  return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`
}

/**
 * Strip formatting from phone number for storage.
 */
function stripPhoneFormatting(value: string): string {
  return value.replace(/\D/g, '')
}

/**
 * Calculate age from date of birth.
 * Returns null if DOB is invalid or in the future.
 */
function calculateAge(dob: string): number | null {
  if (!dob) return null

  const birthDate = new Date(dob)
  if (isNaN(birthDate.getTime())) return null

  const today = new Date()
  if (birthDate > today) return null

  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  const dayDiff = today.getDate() - birthDate.getDate()

  // Adjust age if birthday hasn't occurred this year
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--
  }

  return age
}

/**
 * Get today's date in YYYY-MM-DD format for max attribute
 */
function getTodayString(): string {
  const today = new Date()
  return today.toISOString().split('T')[0]
}

/* --------------------------------------------------------------------------
   Validation Rules
   -------------------------------------------------------------------------- */

export const patientValidationRules = {
  patient_first_name: {
    required: 'First name is required',
    maxLength: {
      value: 100,
      message: 'First name must be 100 characters or less',
    },
    pattern: {
      value: /^[a-zA-Z\s'-]+$/,
      message:
        'First name can only contain letters, spaces, hyphens, and apostrophes',
    },
  },
  patient_last_name: {
    required: 'Last name is required',
    maxLength: {
      value: 100,
      message: 'Last name must be 100 characters or less',
    },
    pattern: {
      value: /^[a-zA-Z\s'-]+$/,
      message:
        'Last name can only contain letters, spaces, hyphens, and apostrophes',
    },
  },
  patient_middle_name: {
    maxLength: {
      value: 100,
      message: 'Middle name must be 100 characters or less',
    },
    pattern: {
      value: /^[a-zA-Z\s'-]*$/,
      message:
        'Middle name can only contain letters, spaces, hyphens, and apostrophes',
    },
  },
  patient_dob: {
    required: 'Date of birth is required',
    validate: {
      notFuture: (value: string) => {
        if (!value) return true
        const dob = new Date(value)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return dob <= today || 'Date of birth cannot be in the future'
      },
      validDate: (value: string) => {
        if (!value) return true
        const date = new Date(value)
        return !isNaN(date.getTime()) || 'Please enter a valid date'
      },
    },
  },
  patient_phone: {
    required: 'Phone number is required',
    validate: {
      validLength: (value: string) => {
        if (!value) return true
        const digits = stripPhoneFormatting(value)
        return (
          digits.length === 10 ||
          'Phone number must be 10 digits'
        )
      },
    },
  },
  patient_email: {
    maxLength: {
      value: 254,
      message: 'Email must be 254 characters or less',
    },
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address',
    },
    validate: {
      // The fax can't print accented letters, so the server rejects them before
      // the @; an international domain is fine (it is sent in its xn-- form)
      asciiLocalPart: (value: string) => {
        if (!value) return true
        return (
          /^[\x21-\x7E]+$/.test(value.split('@')[0]) ||
          'Email can only use unaccented letters, numbers and symbols before the @'
        )
      },
    },
  },
  patient_address_zip: {
    validate: {
      // The form does not know the pharmacy country at rule-definition time, so
      // accept either a US ZIP or a Canadian postal code here (PORTAL-161); the
      // country-specific check runs in AddressFields / on submit.
      validFormat: (value: string) => {
        if (!value) return true
        return (
          validatePostalCode('US', value) === null ||
          validatePostalCode('CA', value) === null ||
          'Enter a valid ZIP or postal code'
        )
      },
    },
  },
  patient_allergies: {
    maxLength: {
      value: 1000,
      message: 'Allergies must be 1000 characters or less',
    },
    validate: {
      requiredIfNotNKA: (value: string, formValues: PatientFormData) => {
        if (!formValues.no_known_allergies && !value?.trim()) {
          return 'Either check "No Known Allergies" or list allergies'
        }
        return true
      },
    },
  },
}

export const animalValidationRules = {
  animal_name: {
    required: 'Pet name is required',
    maxLength: {
      value: 200,
      message: 'Pet name must be 200 characters or less',
    },
  },
  animal_species: {
    required: 'Species is required',
    maxLength: {
      value: 100,
      message: 'Species must be 100 characters or less',
    },
  },
  animal_breed: {
    maxLength: {
      value: 100,
      message: 'Breed must be 100 characters or less',
    },
  },
  owner_first_name: {
    required: 'Owner first name is required',
    maxLength: {
      value: 100,
      message: 'Owner first name must be 100 characters or less',
    },
    validate: (value: string) =>
      value.trim().length > 0 || 'Owner first name is required',
  },
  owner_last_name: {
    required: 'Owner last name is required',
    maxLength: {
      value: 100,
      message: 'Owner last name must be 100 characters or less',
    },
    validate: {
      notBlank: (value: string) =>
        value.trim().length > 0 || 'Owner last name is required',
      // The API stores "first last" in one 200-char field.
      combinedLength: (value: string, values: PatientFormData) =>
        value.trim().length + values.owner_first_name.trim().length + 1 <= 200 ||
        'Owner name must be 200 characters or less combined',
    },
  },
  owner_phone: {
    required: 'Owner phone is required',
    validate: {
      validLength: (value: string) => {
        if (!value) return true
        const digits = stripPhoneFormatting(value)
        return (
          digits.length === 10 ||
          'Phone number must be 10 digits'
        )
      },
    },
  },
  patient_weight: {
    required: 'Weight is required',
    validate: {
      positive: (value: string) => {
        if (!value) return true
        const num = parseFloat(value)
        return (!isNaN(num) && num > 0) || 'Weight must be a positive number'
      },
    },
  },
  patient_weight_unit: {
    required: 'Weight unit is required',
  },
  // Pet DOB reuses the patient_dob field; required unless marked unknown
  patient_dob: {
    validate: {
      requiredIfNotUnknown: (value: string, formValues: PatientFormData) => {
        if (!formValues.animal_dob_unknown && !value) {
          return 'Either enter the date of birth or check "Date of Birth Unknown"'
        }
        return true
      },
      notFuture: (value: string) => {
        if (!value) return true
        const dob = new Date(value)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return dob <= today || 'Date of birth cannot be in the future'
      },
      validDate: (value: string) => {
        if (!value) return true
        const date = new Date(value)
        return !isNaN(date.getTime()) || 'Please enter a valid date'
      },
    },
  },
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export interface PatientFormProps {
  /** Disable all form fields */
  disabled?: boolean
  /** Additional CSS classes */
  className?: string
  /** Pharmacy portal type — controls which patient types are available */
  portalType?: 'human' | 'veterinary' | 'both'
  /** When true, the patient address is required (PORTAL-72). Presentational
   *  only — authoritative enforcement is the submit guard + backend gate. */
  patientAddressRequired?: boolean
  /** Pharmacy country (ISO-3166 alpha-2) — drives State/Province and
   *  ZIP/Postal Code labels and formats on the address fields (PORTAL-161). */
  country?: string
}

/**
 * Patient information form section.
 * Must be used within a react-hook-form FormProvider.
 * Supports both human and animal (veterinary) patient types.
 */
export function PatientForm({
  disabled = false,
  className = '',
  portalType = 'both',
  patientAddressRequired = false,
  country,
}: PatientFormProps) {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<PatientFormData>()

  const patientType = watch('patient_type')
  const dob = watch('patient_dob')
  const age = calculateAge(dob)
  const isMinor = age !== null && age < 18
  const isHuman = patientType === 'human'

  // Show the toggle only when pharmacy supports both patient types
  const showToggle = portalType === 'both'

  // Force the correct patient_type when pharmacy is single-mode
  useEffect(() => {
    if (portalType === 'human' && patientType !== 'human') {
      setValue('patient_type', 'human')
    } else if (portalType === 'veterinary' && patientType !== 'animal') {
      setValue('patient_type', 'animal')
    }
  }, [portalType, patientType, setValue])

  // Clear opposite type's fields when switching
  useEffect(() => {
    if (isHuman) {
      setValue('animal_name', '')
      setValue('animal_species', '')
      setValue('animal_breed', '')
      setValue('owner_first_name', '')
      setValue('owner_last_name', '')
      setValue('owner_phone', '')
      setValue('patient_weight', '')
      setValue('patient_weight_unit', 'lbs')
      setValue('animal_dob_unknown', false)
    } else {
      setValue('patient_first_name', '')
      setValue('patient_last_name', '')
      setValue('patient_middle_name', '')
      setValue('patient_dob', '')
      setValue('patient_phone', '')
      // Nonbinary isn't offered for animals; fall back to Unknown
      if (watch('patient_gender') === 'nonbinary') {
        setValue('patient_gender', 'unknown')
      }
      setValue('patient_address_street', '')
      setValue('patient_address_city', '')
      setValue('patient_address_state', '')
      setValue('patient_address_zip', '')
    }
  }, [isHuman, setValue])

  return (
    <div className={`space-y-5 ${className}`}>
      {/* Patient Type Toggle — only shown when pharmacy supports both types */}
      {showToggle && (
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              // The email is shared by both types, so clear it here on a real
              // switch only; the effect above also runs on mount and prefill
              if (!isHuman) setValue('patient_email', '')
              setValue('patient_type', 'human')
            }}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              isHuman
                ? 'bg-white text-[var(--color-charcoal)] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Human Patient
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              if (isHuman) setValue('patient_email', '')
              setValue('patient_type', 'animal')
            }}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              !isHuman
                ? 'bg-white text-[var(--color-charcoal)] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Animal Patient
          </button>
        </div>
      )}

      {/* Human Patient Fields */}
      {isHuman && (
        <>
          {/* Name row: First, Middle, Last */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
            {/* First Name */}
            <div className="sm:col-span-2">
              <FormField
                label="First Name"
                required
                error={errors.patient_first_name?.message}
                htmlFor="patient_first_name"
              >
                <Input
                  id="patient_first_name"
                  placeholder="First name"
                  disabled={disabled}
                  error={!!errors.patient_first_name}
                  {...register(
                    'patient_first_name',
                    patientValidationRules.patient_first_name
                  )}
                />
              </FormField>
            </div>

            {/* Middle Name */}
            <div className="sm:col-span-2">
              <FormField
                label="Middle Name"
                error={errors.patient_middle_name?.message}
                htmlFor="patient_middle_name"
              >
                <Input
                  id="patient_middle_name"
                  placeholder="Middle name or initial"
                  disabled={disabled}
                  error={!!errors.patient_middle_name}
                  {...register(
                    'patient_middle_name',
                    patientValidationRules.patient_middle_name
                  )}
                />
              </FormField>
            </div>

            {/* Last Name */}
            <div className="sm:col-span-2">
              <FormField
                label="Last Name"
                required
                error={errors.patient_last_name?.message}
                htmlFor="patient_last_name"
              >
                <Input
                  id="patient_last_name"
                  placeholder="Last name"
                  disabled={disabled}
                  error={!!errors.patient_last_name}
                  {...register(
                    'patient_last_name',
                    patientValidationRules.patient_last_name
                  )}
                />
              </FormField>
            </div>
          </div>

          {/* Date of Birth, gender, phone, and email */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <FormField
              label="Date of Birth"
              required
              error={errors.patient_dob?.message}
              htmlFor="patient_dob"
            >
              <div className="space-y-2">
                <Input
                  id="patient_dob"
                  type="date"
                  max={getTodayString()}
                  disabled={disabled}
                  error={!!errors.patient_dob}
                  {...register('patient_dob', patientValidationRules.patient_dob)}
                />
                {age !== null && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">{age} years old</span>
                    {isMinor && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--color-warning-light)] text-[var(--color-warning)] text-xs font-medium">
                        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                        Minor
                      </span>
                    )}
                  </div>
                )}
              </div>
            </FormField>

            <FormField label="Gender" required htmlFor="patient_gender">
              <Controller
                name="patient_gender"
                control={control}
                render={({ field }) => (
                  <Select
                    options={[
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                      { value: 'nonbinary', label: 'Nonbinary' },
                      { value: 'unknown', label: 'Unknown' },
                    ]}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={disabled}
                  />
                )}
              />
            </FormField>

            {/* Phone */}
            <FormField
              label="Phone"
              required
              error={errors.patient_phone?.message}
              htmlFor="patient_phone"
            >
              <Controller
                name="patient_phone"
                control={control}
                rules={patientValidationRules.patient_phone}
                render={({ field }) => (
                  <Input
                    id="patient_phone"
                    type="tel"
                    placeholder="(555) 555-5555"
                    disabled={disabled}
                    error={!!errors.patient_phone}
                    value={formatPhoneNumber(field.value || '')}
                    onChange={(e) =>
                      field.onChange(formatPhoneNumber(e.target.value))
                    }
                    onBlur={field.onBlur}
                  />
                )}
              />
            </FormField>

            {/* Email (optional); two columns wide so a full address fits */}
            <div className="sm:col-span-2">
              <FormField
                label="Email"
                error={errors.patient_email?.message}
                htmlFor="patient_email"
              >
                {/* type="text" so the browser's own email check doesn't
                    replace the inline message; autofill would offer the
                    provider's own email */}
                <Input
                  id="patient_email"
                  type="text"
                  inputMode="email"
                  autoComplete="off"
                  maxLength={254}
                  placeholder="name@example.com"
                  disabled={disabled}
                  error={!!errors.patient_email}
                  {...register('patient_email', patientValidationRules.patient_email)}
                />
              </FormField>
            </div>
          </div>

          {/* Address section with autocomplete */}
          <AddressFields
            formMode
            fieldPrefix="patient_address_"
            disabled={disabled}
            required={patientAddressRequired}
            heading={patientAddressRequired ? 'Address' : 'Address (optional)'}
            country={country}
          />
        </>
      )}

      {/* Animal Patient Fields */}
      {!isHuman && (
        <>
          {/* Pet Name and Species */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField
              label="Pet Name"
              required
              error={errors.animal_name?.message}
              htmlFor="animal_name"
            >
              <Input
                id="animal_name"
                placeholder="Pet name"
                disabled={disabled}
                error={!!errors.animal_name}
                {...register('animal_name', animalValidationRules.animal_name)}
              />
            </FormField>

            <FormField
              label="Species"
              required
              error={errors.animal_species?.message}
              htmlFor="animal_species"
            >
              <Input
                id="animal_species"
                placeholder="e.g. Dog, Cat, Horse"
                disabled={disabled}
                error={!!errors.animal_species}
                {...register(
                  'animal_species',
                  animalValidationRules.animal_species
                )}
              />
            </FormField>
          </div>

          {/* Date of Birth (with Unknown option, mirroring the allergies flow) */}
          <div>
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input
                type="checkbox"
                disabled={disabled}
                className="h-4 w-4 rounded border-gray-300 text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-secondary)]"
                {...register('animal_dob_unknown', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      setValue('patient_dob', '')
                    }
                  },
                })}
              />
              <span className="text-sm font-medium text-[var(--color-charcoal)]">
                Date of Birth Unknown
              </span>
            </label>

            {!watch('animal_dob_unknown') && (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <FormField
                  label="Date of Birth"
                  required
                  error={errors.patient_dob?.message}
                  htmlFor="patient_dob"
                >
                  <Input
                    id="patient_dob"
                    type="date"
                    max={getTodayString()}
                    disabled={disabled}
                    error={!!errors.patient_dob}
                    {...register('patient_dob', animalValidationRules.patient_dob)}
                  />
                </FormField>
              </div>
            )}
          </div>

          {/* Breed, gender, and weight */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <FormField
              label="Breed"
              error={errors.animal_breed?.message}
              htmlFor="animal_breed"
            >
              <Input
                id="animal_breed"
                placeholder="Breed (optional)"
                disabled={disabled}
                error={!!errors.animal_breed}
                {...register('animal_breed', animalValidationRules.animal_breed)}
              />
            </FormField>

            <FormField label="Gender" required htmlFor="patient_gender">
              <Controller
                name="patient_gender"
                control={control}
                render={({ field }) => (
                  <Select
                    options={[
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                      { value: 'unknown', label: 'Unknown' },
                    ]}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={disabled}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Weight"
              required
              error={errors.patient_weight?.message}
              htmlFor="patient_weight"
            >
              <div className="flex gap-2">
                <Input
                  id="patient_weight"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="e.g. 45.5"
                  disabled={disabled}
                  error={!!errors.patient_weight}
                  className="flex-1"
                  {...register('patient_weight', animalValidationRules.patient_weight)}
                />
                <select
                  disabled={disabled}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[var(--color-deep-purple)] focus:outline-none focus:ring-1 focus:ring-[var(--color-deep-purple)]"
                  {...register('patient_weight_unit', animalValidationRules.patient_weight_unit)}
                >
                  <option value="lbs">lbs</option>
                  <option value="kg">kg</option>
                </select>
              </div>
            </FormField>
          </div>

          {/* Owner Name, Phone, and Email */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField
              label="Owner First Name"
              required
              error={errors.owner_first_name?.message}
              htmlFor="owner_first_name"
            >
              <Input
                id="owner_first_name"
                placeholder="Pet owner's first name"
                disabled={disabled}
                error={!!errors.owner_first_name}
                {...register('owner_first_name', animalValidationRules.owner_first_name)}
              />
            </FormField>

            <FormField
              label="Owner Last Name"
              required
              error={errors.owner_last_name?.message}
              htmlFor="owner_last_name"
            >
              <Input
                id="owner_last_name"
                placeholder="Pet owner's last name"
                disabled={disabled}
                error={!!errors.owner_last_name}
                {...register('owner_last_name', animalValidationRules.owner_last_name)}
              />
            </FormField>

            <FormField
              label="Owner Phone"
              required
              error={errors.owner_phone?.message}
              htmlFor="owner_phone"
            >
              <Controller
                name="owner_phone"
                control={control}
                rules={animalValidationRules.owner_phone}
                render={({ field }) => (
                  <Input
                    id="owner_phone"
                    type="tel"
                    placeholder="(555) 555-5555"
                    disabled={disabled}
                    error={!!errors.owner_phone}
                    value={formatPhoneNumber(field.value || '')}
                    onChange={(e) =>
                      field.onChange(formatPhoneNumber(e.target.value))
                    }
                    onBlur={field.onBlur}
                  />
                )}
              />
            </FormField>

            {/* Owner Email (optional) — same field as the human patient's Email */}
            <FormField
              label="Owner Email"
              error={errors.patient_email?.message}
              htmlFor="patient_email"
            >
              <Input
                id="patient_email"
                type="text"
                inputMode="email"
                autoComplete="off"
                maxLength={254}
                placeholder="name@example.com"
                disabled={disabled}
                error={!!errors.patient_email}
                {...register('patient_email', patientValidationRules.patient_email)}
              />
            </FormField>
          </div>
        </>
      )}

      {/* Allergies (shown for both types) */}
      <div>
        {/* NKA Checkbox */}
        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            disabled={disabled}
            className="h-4 w-4 rounded border-gray-300 text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-secondary)]"
            {...register('no_known_allergies', {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                  setValue('patient_allergies', '')
                }
              },
            })}
          />
          <span className="text-sm font-medium text-[var(--color-charcoal)]">
            No Known Allergies (NKA)
          </span>
        </label>

        {/* Allergies textarea (hidden when NKA checked) */}
        {!watch('no_known_allergies') && (
          <FormField
            label="Allergies"
            error={errors.patient_allergies?.message}
            htmlFor="patient_allergies"
            helperText="List any known drug allergies"
          >
            <Textarea
              id="patient_allergies"
              placeholder="List allergies..."
              rows={3}
              disabled={disabled}
              error={!!errors.patient_allergies}
              {...register(
                'patient_allergies',
                patientValidationRules.patient_allergies
              )}
            />
          </FormField>
        )}
      </div>

      {/* Current/Other Medications */}
      <div>
        {/* No Current Medications Checkbox */}
        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            disabled={disabled}
            className="h-4 w-4 rounded border-gray-300 text-[var(--pharmacy-primary)] focus:ring-[var(--pharmacy-secondary)]"
            {...register('no_current_medications', {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                  setValue('current_medications', '')
                }
              },
            })}
          />
          <span className="text-sm font-medium text-[var(--color-charcoal)]">
            Not Taking Any Medications
          </span>
        </label>

        {/* Medications textarea (hidden when checkbox checked) */}
        {!watch('no_current_medications') && (
          <FormField
            label="Current/Other Medications"
            error={errors.current_medications?.message}
            htmlFor="current_medications"
            helperText="List any current medications the patient is taking"
          >
            <Textarea
              id="current_medications"
              placeholder="List current medications..."
              rows={3}
              disabled={disabled}
              error={!!errors.current_medications}
              {...register('current_medications', {
                maxLength: {
                  value: 2000,
                  message: 'Current medications must be 2000 characters or less',
                },
                validate: {
                  requiredIfNotNone: (value: string) => {
                    if (!watch('no_current_medications') && !value?.trim()) {
                      return 'Either check "Not Taking Any Medications" or list medications'
                    }
                    return true
                  },
                },
              })}
            />
          </FormField>
        )}
      </div>
    </div>
  )
}

export default PatientForm
